// The modal stack (ui-flow §2, §15): modals stack over any state inside #modal-root, trap focus
// and return it on close; Esc closes the top one when it is closable.
//
// Implementer: client UI. Consumers: ui/modals/* (settings, summary, dialogs), app.ts (Esc
// routing: `closeTopModal()` first).
//
// Esc itself is routed by the app (KeyboardInput → closeTopModal); this module handles Tab
// trapping, the backdrop click and the ✕ button.
//
// Initial focus: the first control (not the ✕) when the modal was opened from the keyboard; the
// dialog box itself when it was opened with a pointer, so no focus ring shows on a mouse or touch
// open (agar shows none; the ring is for keyboard focus, ui-qol §1). Tab then enters the controls.

import { h } from '../dom.js';
import { t } from '../i18n/t.js';

export interface ModalOptions {
  /** Esc / backdrop / ✕ close it (system dialogs are not closable). Default true. */
  readonly closable?: boolean;
  /** Accessible label. */
  readonly label?: string;
  readonly onClose?: () => void;
  /** Extra class on the dialog box (e.g. `modal-settings`). */
  readonly className?: string;
  /** The × was clicked (the click sound, audio §3 row 7); Esc and the backdrop stay silent. */
  readonly onCloseClick?: () => void;
}

export interface ModalHandle {
  readonly el: HTMLElement;
  close(): void;
  readonly open: boolean;
}

interface Entry {
  readonly handle: ModalHandle;
  readonly layer: HTMLElement;
  readonly closable: boolean;
}

let root: HTMLElement | null = null;
const stack: Entry[] = [];
/** How the user last interacted (document capture listeners, installed on the first open). */
let lastModality: 'keyboard' | 'pointer' = 'keyboard';
let modalityDoc: Document | null = null;

function trackModality(doc: Document): void {
  if (modalityDoc === doc) return;
  modalityDoc = doc;
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  doc.addEventListener('keydown', () => (lastModality = 'keyboard'), opts);
  doc.addEventListener('pointerdown', () => (lastModality = 'pointer'), opts);
  doc.addEventListener('touchstart', () => (lastModality = 'pointer'), opts);
}
/** Siblings of the modal root made inert while any modal is open (restored afterwards). */
let inerted: HTMLElement[] = [];

function setBackgroundInert(on: boolean): void {
  if (!on) {
    for (const el of inerted) el.removeAttribute('inert');
    inerted = [];
    return;
  }
  const parent = root?.parentElement;
  if (!root || !parent || inerted.length > 0) return;
  for (const child of Array.from(parent.children)) {
    if (child === root || !(child instanceof HTMLElement) || child.hasAttribute('inert')) continue;
    if (child.id === 'toast-root' || child.id === 'privy-island' || child.tagName === 'SCRIPT') {
      continue;
    }
    child.setAttribute('inert', '');
    inerted.push(child);
  }
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hidden && el.getAttribute('aria-hidden') !== 'true',
  );
}

/** Sets the container (#modal-root) once at boot. */
export function initModals(rootEl: HTMLElement): void {
  root = rootEl;
  if (rootEl.ownerDocument) trackModality(rootEl.ownerDocument);
  root.classList.add('modal-root');
}

/** Pushes `content` as a new top modal. */
export function openModal(content: HTMLElement, opts: ModalOptions = {}): ModalHandle {
  const closable = opts.closable ?? true;
  const doc = content.ownerDocument;
  const returnFocus = doc.activeElement instanceof HTMLElement ? doc.activeElement : null;
  let isOpen = true;

  const dialog = h(
    'div',
    {
      class: `modal card${opts.className ? ` ${opts.className}` : ''}`,
      role: closable ? 'dialog' : 'alertdialog',
      'aria-modal': 'true',
      'aria-label': opts.label ?? null,
      tabindex: '-1',
    },
    closable
      ? h(
          'button',
          {
            type: 'button',
            class: 'modal-close',
            'aria-label': t('common.close'),
            onClick: () => {
              opts.onCloseClick?.();
              handle.close();
            },
          },
          '×',
        )
      : null,
    content,
  );
  const backdrop = h('div', {
    class: 'modal-backdrop',
    onClick: () => {
      if (closable) handle.close();
    },
  });
  const layer = h('div', { class: 'modal-layer' }, backdrop, dialog);

  layer.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key !== 'Tab') return;
    const items = focusables(dialog);
    if (items.length === 0) {
      ev.preventDefault();
      dialog.focus();
      return;
    }
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    const active = doc.activeElement;
    if (ev.shiftKey && (active === first || active === dialog)) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && active === last) {
      ev.preventDefault();
      first.focus();
    }
  });

  const handle: ModalHandle = {
    el: dialog,
    get open() {
      return isOpen;
    },
    close() {
      if (!isOpen) return;
      isOpen = false;
      const i = stack.findIndex((e) => e.handle === handle);
      if (i >= 0) stack.splice(i, 1);
      layer.remove();
      const top = stack[stack.length - 1];
      if (top) {
        top.layer.removeAttribute('inert');
        if (!top.layer.contains(doc.activeElement)) top.handle.el.focus();
      } else {
        setBackgroundInert(false);
        if (returnFocus && returnFocus.isConnected) returnFocus.focus();
      }
      opts.onClose?.();
    },
  };

  const below = stack[stack.length - 1];
  if (below) below.layer.setAttribute('inert', '');
  stack.push({ handle, layer, closable });
  (root ?? doc.body).appendChild(layer);
  setBackgroundInert(true);

  // Focus the first control that is not the ✕ (fall back to the ✕, then the dialog itself);
  // after a pointer open, the dialog itself (see the header).
  const items = focusables(dialog);
  const preferred = items.find((el) => !el.classList.contains('modal-close')) ?? items[0];
  if (lastModality === 'pointer' || !preferred) dialog.focus({ preventScroll: true });
  else preferred.focus();
  trackModality(doc);
  return handle;
}

/** Closes the top modal if it is closable; true if one was closed. */
export function closeTopModal(): boolean {
  const top = stack[stack.length - 1];
  if (!top || !top.closable) return false;
  top.handle.close();
  return true;
}

export function hasOpenModal(): boolean {
  return stack.length > 0;
}

/** Closes every modal, closable or not (app stop). */
export function closeAllModals(): void {
  for (let top = stack[stack.length - 1]; top; top = stack[stack.length - 1]) top.handle.close();
}
